import {clearCancelToken, getCancelToken} from "api/cancelTokens";
import mapXCandidateApi from "api/candidateApi";
import mapxProjectInsightsApi from "api/projectApi/projectStatsApi";
import {
	GET_SELECTED_PROJECT_SAVED_PEOPLE_INSIGHTS_TOKEN,
	PROJECT_DIVERSITY_REPORT_TOKEN,
	PROJECT_ETHNICITY_REPORT_TOKEN,
	PROJECT_FIT_TO_BRIEF_REPORT_TOKEN,
	PROJECT_FREQ_HIRED_FROM_REPORT_TOKEN,
	PROJECT_INDUSTRIES_BACKGROUND_REPORT_TOKEN,
	PROJECT_JOB_FUNCTION_REPORT_TOKEN,
	PROJECT_LOCATION_REPORT_TOKEN,
} from "api/requestCancelTokenStrings";
import axios, {AxiosResponse} from "axios";
import {buildCandidateApiRequestPayload} from "helpers/filterHandlers";
import {successResponse} from "helpers/map";
import {noop} from "lodash";
import {TProjectInsightsFilter} from "mapx-pages/Project/Insights/ProjectInsightsFilter/types";
import {
	getIndustriesBackgroundReportDone,
	getIndustriesBackgroundReportFailed,
	getIndustriesBackgroundReportInit,
	setProjectFrequentlyHiredFromData,
} from "store/project/projectActions";
import {
	setCountryReport,
	setEthnicDiversityData,
	setFitToBriefData,
	setGenderDiversityData,
	setJobFunctionDistributionData,
	setJobFunctionDistributionInit,
} from "store/project/ProjectInsights/projectInsightsActions";
import {projectInsightSelectedFiltersSelector} from "store/project/ProjectInsights/projectInsightSelectors";
import {targetListIDSelector} from "store/target-list/targetListSelectors";
import {TAppDispatch, TRootState} from "types";

export const getGenderDiversityData =
	(id: number, callback?: () => void) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const cancelTokenSource = getCancelToken(PROJECT_DIVERSITY_REPORT_TOKEN);

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		const payload = buildCandidateApiRequestPayload(filters);
		const formData = {filters: payload?.filters};

		let data;

		try {
			const response = await mapxProjectInsightsApi.getGenderDiversityReport(id, formData, {
				cancelToken: cancelTokenSource.token,
			});

			if (response && successResponse(response, 200)) {
				data = response.data;
			} else {
				data = [];
			}

			dispatch(setGenderDiversityData(data));
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
			}

			callback && callback();
		} finally {
			callback && callback();
			clearCancelToken(PROJECT_DIVERSITY_REPORT_TOKEN);
		}
	};
export const getEthnicDiversityData =
	(id: number, callback?: () => void) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const cancelTokenSource = getCancelToken(PROJECT_ETHNICITY_REPORT_TOKEN);

		let data;

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		const payload = buildCandidateApiRequestPayload(filters);
		const formData = {filters: payload?.filters};

		try {
			const response = await mapxProjectInsightsApi.getEthnicDiversityReport(id, formData, {
				cancelToken: cancelTokenSource.token,
			});

			if (response && successResponse(response, 200)) {
				data = response.data;
			} else {
				data = [];
			}

			dispatch(setEthnicDiversityData(data));
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
			}
		} finally {
			callback && callback();

			clearCancelToken(PROJECT_ETHNICITY_REPORT_TOKEN);
		}
	};

export const getFitToBriefData =
	(projectId: number, callback?: () => void) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const cancelTokenSource = getCancelToken(PROJECT_FIT_TO_BRIEF_REPORT_TOKEN);

		let data;

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		const payload = buildCandidateApiRequestPayload(filters);
		const filtersData = {filters: payload?.filters} as TProjectInsightsFilter;

		try {
			const response = await mapxProjectInsightsApi.getFitToBriefReport(
				filtersData,
				projectId,
				{
					cancelToken: cancelTokenSource.token,
				},
			);

			if (response && successResponse(response, 200)) {
				data = response.data;
			} else {
				data = [];
			}

			dispatch(setFitToBriefData(data));
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
			}
		} finally {
			callback && callback();

			clearCancelToken(PROJECT_FIT_TO_BRIEF_REPORT_TOKEN);
		}
	};

export const getCountryReport =
	(id: number, callback?: () => void) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const cancelTokenSource = getCancelToken(PROJECT_LOCATION_REPORT_TOKEN);

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);
		const payload = buildCandidateApiRequestPayload(filters);
		const formData = {filters: payload?.filters};

		let data = [];

		try {
			const response: AxiosResponse = await mapxProjectInsightsApi.getCountryReport(
				id,
				formData,
				{
					cancelToken: cancelTokenSource.token,
				},
			);

			if (response && successResponse(response, 200)) {
				data = response.data.results;
			} else {
				data = [];
			}

			dispatch(setCountryReport(data));
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
			}
		} finally {
			callback && callback();

			clearCancelToken(PROJECT_LOCATION_REPORT_TOKEN);
		}
	};
export const getJobFunctionDistributionReport =
	(id: number) => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		dispatch(setJobFunctionDistributionInit());

		const cancelTokenSource = getCancelToken(PROJECT_JOB_FUNCTION_REPORT_TOKEN);

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		const payload = buildCandidateApiRequestPayload(filters);
		const formData = {filters: payload?.filters};

		try {
			const response: AxiosResponse = await mapxProjectInsightsApi.getJobFunctionDistribution(
				id,
				formData,
				{
					cancelToken: cancelTokenSource.token,
				},
			);

			if (successResponse(response, 200)) {
				const data = response.data.results;

				dispatch(setJobFunctionDistributionData(data));
			}
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
			}
		} finally {
			clearCancelToken(PROJECT_JOB_FUNCTION_REPORT_TOKEN);
		}
	};
export const getIndustriesBackgroundReport =
	(id: number) => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		dispatch(getIndustriesBackgroundReportInit());

		const cancelTokenSource = getCancelToken(PROJECT_INDUSTRIES_BACKGROUND_REPORT_TOKEN);

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		const payload = buildCandidateApiRequestPayload(filters);
		const formData = {filters: payload?.filters};

		try {
			const response: AxiosResponse =
				await mapxProjectInsightsApi.getIndustriesBackgroundReport(id, formData, {
					cancelToken: cancelTokenSource.token,
				});

			if (successResponse(response, 200)) {
				const data = response.data.results;

				dispatch(getIndustriesBackgroundReportDone(data));
			}
		} catch (error) {
			if (axios.isCancel(error)) {
				console.log("Request canceled:", error.message);
			} else {
				console.error(error);
			}

			dispatch(getIndustriesBackgroundReportFailed());

			return {error};
		} finally {
			clearCancelToken(PROJECT_INDUSTRIES_BACKGROUND_REPORT_TOKEN);
		}
	};

export const getProjectFrequentlyHiredFromData =
	(id: number, pagination: {page: number; per_page: number}, callback = noop) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const cancelTokenSource = getCancelToken(PROJECT_FREQ_HIRED_FROM_REPORT_TOKEN);

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		const payload = buildCandidateApiRequestPayload(filters);
		const formData = {
			filters: payload?.filters,
			pagination,
		};

		try {
			const response = await mapxProjectInsightsApi.getProjectFrequentlyHiredFrom(
				id,
				formData,
				{
					cancelToken: cancelTokenSource.token,
				},
			);

			if (response && successResponse(response, 200)) {
				const data = response.data;

				dispatch(setProjectFrequentlyHiredFromData(data));
			}
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
			}
		} finally {
			callback && callback();

			clearCancelToken(PROJECT_FREQ_HIRED_FROM_REPORT_TOKEN);
		}
	};

export const getSelectedProjectSavedPeopleForInsights =
	() => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const cancelTokenSource = getCancelToken(GET_SELECTED_PROJECT_SAVED_PEOPLE_INSIGHTS_TOKEN);

		const state = getState();

		const filters = projectInsightSelectedFiltersSelector(state);

		try {
			const payload = buildCandidateApiRequestPayload({...filters}, true);

			const targetListID = targetListIDSelector(state);

			const apiPayload = {
				filters: {target_list: targetListID},
				pagination: {
					page: 1,
					per_page: 1,
				},
			};

			if (payload) {
				apiPayload.filters = {
					...apiPayload.filters,
					...payload.filters,
				};
			}

			const response = await mapXCandidateApi.getCandidatesByFilter(apiPayload, {
				cancelToken: cancelTokenSource.token,
			});

			if (response && successResponse(response, 200)) {
				return response.data.pagination;
			} else {
				return null;
			}
		} catch (error) {
			console.error("Error in getSelectedProjectSavedPeopleForInsights:", error);

			return null;
		}
	};
